import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UsersManager, PaymentModesManager, BusinessSettings } from "@/components/SettingsManagers";
import SmsSettings from "@/components/SmsSettings";
import { AssistantMemory } from "@/components/assistant/AssistantMemory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Bank, Buildings, Warning, TrashSimple, DownloadSimple, UploadSimple, Database, Lock, ShieldCheck, Key, ChatCircleText } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CostingSettingsCard } from "@/components/SheetBilling";
import { lockAllSections } from "@/components/PinLock";
import { istToday } from "@/lib/istDate";
import PhoneCleanupPanel from "@/components/PhoneCleanupPanel";

export default function SettingsPage() {
  const { user } = useAuth();
  const isPrimaryAdmin = user?.role === "admin";
  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs font-mono-num tracking-widest text-slate-400">// SETTINGS</div>
        <h1 className="font-heading text-3xl font-black text-white">Workshop Setup</h1>
        <p className="text-slate-400 text-sm mt-1">
          Company details, staff logins and payment methods.
        </p>
      </div>
      {isPrimaryAdmin && <CostingSettingsCard />}

      <Tabs defaultValue="business">
        <TabsList className="bg-slate-800/60 border border-slate-700 rounded-sm h-11 flex-wrap">
          <TabsTrigger value="business" data-testid="settings-tab-business"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <Buildings size={14} className="mr-1.5" /> COMPANY INFO
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="settings-tab-users"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <Users size={14} className="mr-1.5" /> USERS
          </TabsTrigger>
          <TabsTrigger value="payment-modes" data-testid="settings-tab-modes"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <Bank size={14} className="mr-1.5" /> PAYMENT METHODS
          </TabsTrigger>
          <TabsTrigger value="data" data-testid="settings-tab-data"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <Database size={14} className="mr-1.5" /> DATA
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <Lock size={14} className="mr-1.5" /> SECURITY
          </TabsTrigger>
          <TabsTrigger value="assistant" data-testid="settings-tab-assistant"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            HEY SHOBHIT MEMORY
          </TabsTrigger>
          <TabsTrigger value="sms" data-testid="settings-tab-sms"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
            <ChatCircleText size={14} className="mr-1.5" /> SMS
          </TabsTrigger>
          {isPrimaryAdmin && (
            <TabsTrigger value="danger" data-testid="settings-tab-danger"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs">
              <Warning size={14} className="mr-1.5" /> DANGER ZONE
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="business" className="mt-6">
          <BusinessSettings />
        </TabsContent>
        <TabsContent value="users" className="mt-6">
          <UsersManager />
        </TabsContent>
        <TabsContent value="payment-modes" className="mt-6">
          <PaymentModesManager />
        </TabsContent>
        <TabsContent value="data" className="mt-6">
          {isPrimaryAdmin && <div className="mb-4"><PhoneCleanupPanel /></div>}
          <DataPortability isPrimaryAdmin={isPrimaryAdmin} />
        </TabsContent>
        <TabsContent value="security" className="mt-6">
          <SecuritySettings />
        </TabsContent>
        <TabsContent value="assistant" className="mt-6">
          <AssistantMemory />
        </TabsContent>
        <TabsContent value="sms" className="mt-6">
          <SmsSettings />
        </TabsContent>
        {isPrimaryAdmin && (
          <TabsContent value="danger" className="mt-6">
            <DangerZone />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ---------------- Danger Zone ---------------- */
function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [includePurchases, setIncludePurchases] = useState(false);
  const [busy, setBusy] = useState(false);

  const doReset = async () => {
    if (confirm !== "RESET") {
      toast.error('Type "RESET" exactly to confirm');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-sale-data", {
        confirm: "RESET",
        include_purchases: includePurchases,
      });
      const parts = Object.entries(data.deleted).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Reset done — cleared: ${parts}`);
      setOpen(false);
      setConfirm("");
      // Refresh so widgets re-fetch
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="danger-zone">
      <div className="surface p-6 rounded-sm border-2 border-red-500/40 bg-red-500/5">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <Warning size={28} className="text-red-400" weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono-num tracking-widest text-red-400">// DANGER ZONE — IRREVERSIBLE</div>
            <h2 className="font-heading text-2xl font-black text-white mt-1">Reset Sale Data</h2>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Clears <b>all orders, all welders/customers, all estimates, and the order counter</b>.
              Order numbering will restart from <span className="font-mono-num text-orange-400">#SC-2026-1001</span>.
              <br/><br/>
              <b className="text-emerald-400">Kept safe:</b> Items master, user logins, business settings, and payment methods.
              <br/>
              Optional: also delete purchase/expense records for a fully clean P&amp;L.
            </p>
            <div className="mt-4">
              <Button
                onClick={() => setOpen(true)}
                className="bg-red-600 hover:bg-red-500 text-white rounded-sm h-11 px-5 font-heading tracking-widest"
                data-testid="open-reset-dialog"
              >
                <TrashSimple size={18} className="mr-2" weight="bold" />
                RESET SALE DATA
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-slate-900 border-red-500/40 text-white rounded-sm" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-400">
              <Warning size={22} weight="fill" /> Confirm Data Reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              This will permanently delete all sale data. Type <b className="text-red-400 font-mono-num">RESET</b> below to confirm.
            </p>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">TYPE "RESET" TO CONFIRM</Label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="RESET"
                className="bg-slate-800/60 border-red-500/40 text-white h-11 rounded-sm font-mono-num mt-1.5"
                data-testid="reset-confirm-input"
                autoFocus
              />
            </div>
            <label className="flex items-start gap-2 p-3 border border-slate-700 rounded-sm cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={includePurchases}
                onChange={(e) => setIncludePurchases(e.target.checked)}
                className="mt-0.5 accent-red-500"
                data-testid="reset-include-purchases"
              />
              <div>
                <div className="text-slate-200">Also delete purchase history</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Removes stock purchases too — for a fully clean start.
                </div>
              </div>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={() => { setOpen(false); setConfirm(""); }}
                variant="outline"
                className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={doReset}
                disabled={busy || confirm !== "RESET"}
                className="bg-red-600 hover:bg-red-500 text-white rounded-sm h-11 px-5 font-heading tracking-widest disabled:opacity-40"
                data-testid="reset-confirm-btn"
              >
                {busy ? "RESETTING…" : "DELETE ALL"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- Data Portability (Export / Import) ---------------- */
function DataPortability({ isPrimaryAdmin }) {
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importConfirm, setImportConfirm] = useState("");
  const [importing, setImporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = istToday();
      a.href = url;
      a.download = `shobhit-creation-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const collections = data?.collections || {};
      const totals = Object.entries(collections).map(([k, v]) => `${(v || []).length} ${k}`).join(", ");
      toast.success(`Backup downloaded — ${totals}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setExporting(false); }
  };

  const doImport = async () => {
    if (!importFile) { toast.error("Choose a backup JSON file first"); return; }
    const expected = importMode === "replace" ? "IMPORT" : "MERGE";
    if (importConfirm !== expected) {
      toast.error(`Type "${expected}" exactly to confirm`);
      return;
    }
    setImporting(true);
    try {
      const text = await importFile.text();
      const parsed = JSON.parse(text);
      const { data } = await api.post("/admin/import", {
        data: parsed,
        mode: importMode,
        confirm: importConfirm,
      });
      const parts = Object.entries(data.imported || {}).map(([k, v]) => `${v} ${k}`).join(", ");
      toast.success(`Imported ${importMode.toUpperCase()} — ${parts}`);
      setImportOpen(false);
      setImportFile(null);
      setImportConfirm("");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      if (e instanceof SyntaxError) {
        toast.error("Invalid backup file — could not parse JSON");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="data-portability">
      <div className="surface p-6 rounded-sm border-l-2 border-emerald-500/60">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <DownloadSimple size={28} className="text-emerald-400" weight="bold" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono-num tracking-widest text-emerald-400">// EXPORT</div>
            <h2 className="font-heading text-2xl font-black text-white mt-1">Download Full Backup</h2>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Exports <b>every order, welder, item, purchase, estimate, setting</b> and the recycle bin
              into a single JSON file. Keep it safe — you can import it back anytime.
            </p>
            <div className="mt-4">
              <Button
                onClick={doExport}
                disabled={exporting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm h-11 px-5 font-heading tracking-widest"
                data-testid="export-data-btn"
              >
                <DownloadSimple size={18} className="mr-2" weight="bold" />
                {exporting ? "PREPARING…" : "DOWNLOAD BACKUP (.JSON)"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className={"surface p-6 rounded-sm border-l-2 " + (isPrimaryAdmin ? "border-cyan-500/60" : "border-slate-700 opacity-70")}>
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <UploadSimple size={28} className="text-cyan-400" weight="bold" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-mono-num tracking-widest text-cyan-400">// IMPORT</div>
            <h2 className="font-heading text-2xl font-black text-white mt-1">Restore From Backup</h2>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Upload a previous backup JSON. Choose <b className="text-emerald-400">Merge</b> to add missing records
              on top of existing data, or <b className="text-red-400">Replace</b> to wipe every collection and load
              the backup fresh.
            </p>
            {!isPrimaryAdmin && (
              <p className="text-[11px] text-amber-400 mt-2">
                Only the primary admin can import data.
              </p>
            )}
            <div className="mt-4">
              <Button
                onClick={() => setImportOpen(true)}
                disabled={!isPrimaryAdmin}
                className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-sm h-11 px-5 font-heading tracking-widest disabled:opacity-40"
                data-testid="open-import-dialog"
              >
                <UploadSimple size={18} className="mr-2" weight="bold" />
                IMPORT BACKUP
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportConfirm(""); } }}>
        <DialogContent className="max-w-md bg-slate-900 border-cyan-500/40 text-white rounded-sm" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-cyan-400">
              <UploadSimple size={22} weight="bold" /> Import Backup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">BACKUP FILE (.JSON)</Label>
              <Input
                type="file"
                accept="application/json,.json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1.5 file:mr-3 file:py-1 file:px-3 file:rounded-sm file:border-0 file:text-xs file:bg-slate-700 file:text-white"
                data-testid="import-file-input"
              />
              {importFile && (
                <div className="text-[11px] text-slate-400 mt-1 font-mono-num">
                  {importFile.name} · {(importFile.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest mb-1.5 block">MODE</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { setImportMode("merge"); setImportConfirm(""); }}
                  className={
                    "p-3 rounded-sm border text-left transition-colors " +
                    (importMode === "merge"
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                      : "border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600")
                  }
                  data-testid="import-mode-merge"
                >
                  <div className="font-heading text-sm font-black">MERGE</div>
                  <div className="text-[10px] font-mono-num opacity-80 mt-1">
                    Add new records, keep existing ones
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => { setImportMode("replace"); setImportConfirm(""); }}
                  className={
                    "p-3 rounded-sm border text-left transition-colors " +
                    (importMode === "replace"
                      ? "border-red-500 bg-red-500/10 text-red-300"
                      : "border-slate-700 bg-slate-800/40 text-slate-300 hover:border-slate-600")
                  }
                  data-testid="import-mode-replace"
                >
                  <div className="font-heading text-sm font-black">REPLACE</div>
                  <div className="text-[10px] font-mono-num opacity-80 mt-1">
                    Wipe everything, load fresh
                  </div>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                TYPE "{importMode === "replace" ? "IMPORT" : "MERGE"}" TO CONFIRM
              </Label>
              <Input
                value={importConfirm}
                onChange={(e) => setImportConfirm(e.target.value)}
                placeholder={importMode === "replace" ? "IMPORT" : "MERGE"}
                className={
                  "bg-slate-800/60 text-white h-11 rounded-sm font-mono-num mt-1.5 " +
                  (importMode === "replace" ? "border-red-500/40" : "border-emerald-500/40")
                }
                data-testid="import-confirm-input"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={() => setImportOpen(false)}
                variant="outline"
                className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={doImport}
                disabled={importing || !importFile || importConfirm !== (importMode === "replace" ? "IMPORT" : "MERGE")}
                className={
                  "text-white rounded-sm h-11 px-5 font-heading tracking-widest disabled:opacity-40 " +
                  (importMode === "replace"
                    ? "bg-red-600 hover:bg-red-500"
                    : "bg-cyan-600 hover:bg-cyan-500")
                }
                data-testid="import-confirm-btn"
              >
                {importing ? "IMPORTING…" : (importMode === "replace" ? "REPLACE ALL" : "MERGE DATA")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Security (Owner PIN) ---------------- */
function SecuritySettings() {
  const [info, setInfo] = useState({ is_custom: false, updated_at: null });
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const loadInfo = async () => {
    try {
      const { data } = await api.get("/settings/owner-pin/info");
      setInfo(data || { is_custom: false });
    } catch (_e) { /* keep defaults */ }
  };
  React.useEffect(() => { loadInfo(); }, []);

  const clean = (v) => (v || "").replace(/\D/g, "").slice(0, 4);

  const submit = async () => {
    if (!/^\d{4}$/.test(oldPin)) { toast.error("Current PIN 4 digits ka chahiye"); return; }
    if (!/^\d{4}$/.test(newPin)) { toast.error("New PIN 4 digits ka chahiye"); return; }
    if (newPin !== confirmPin) { toast.error("New PIN aur confirm PIN match nahi ho rahe"); return; }
    if (oldPin === newPin) { toast.error("New PIN current PIN se alag hona chahiye"); return; }
    setBusy(true);
    try {
      await api.patch("/settings/owner-pin", { old_pin: oldPin, new_pin: newPin });
      toast.success("Owner PIN update ho gaya. Sabhi devices pe naya PIN chalega.");
      setOldPin(""); setNewPin(""); setConfirmPin("");
      // Force everyone (including this session) to re-enter PIN with new value
      lockAllSections();
      loadInfo();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const PinField = ({ label, value, onChange, testid }) => (
    <div>
      <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">{label}</Label>
      <Input
        type={showAll ? "text" : "password"}
        inputMode="numeric"
        maxLength={4}
        value={value}
        onChange={(e) => onChange(clean(e.target.value))}
        placeholder="• • • •"
        className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg tracking-[0.5em] text-center mt-1.5"
        data-testid={testid}
        autoComplete="off"
      />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="security-settings">
      <div className="surface p-6 rounded-sm border-l-2 border-orange-500/60">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            <Key size={28} className="text-orange-400" weight="bold" />
          </div>
          <div className="flex-1 max-w-lg">
            <div className="flex items-center gap-2">
              <div className="text-xs font-mono-num tracking-widest text-orange-400">// OWNER PIN</div>
              {info.is_custom ? (
                <span className="text-[10px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/10 border border-emerald-500/40 text-emerald-300">
                  CUSTOM PIN SET
                </span>
              ) : (
                <span className="text-[10px] font-mono-num tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-500/10 border border-amber-500/40 text-amber-300">
                  USING DEFAULT PIN
                </span>
              )}
            </div>
            <h2 className="font-heading text-2xl font-black text-white mt-1">Change Owner PIN</h2>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Ye PIN <b>Reports, Operators, Recycle Bin</b> aur <b>Settings</b> ko protect karta hai.
              Agar staff ne current PIN dekh liya hai to yaha se turant rotate kar dijiye.
              {!info.is_custom && (
                <> Default PIN <span className="font-mono-num text-orange-300">1380</span> chal raha hai —
                naya set karne ke liye Current PIN mein <span className="font-mono-num">1380</span> daaliye.</>
              )}
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3">
              <PinField label="CURRENT PIN" value={oldPin} onChange={setOldPin} testid="pin-old-input" />
              <PinField label="NEW PIN (4 DIGITS)" value={newPin} onChange={setNewPin} testid="pin-new-input" />
              <PinField label="CONFIRM NEW PIN" value={confirmPin} onChange={setConfirmPin} testid="pin-confirm-input" />

              <label className="flex items-center gap-2 text-[11px] text-slate-400 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAll}
                  onChange={(e) => setShowAll(e.target.checked)}
                  className="accent-orange-500"
                  data-testid="pin-show-toggle"
                />
                Digits show karein
              </label>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={submit}
                disabled={busy || oldPin.length < 4 || newPin.length < 4 || confirmPin.length < 4}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm h-11 px-5 font-heading tracking-widest disabled:opacity-40"
                data-testid="pin-change-submit"
              >
                <ShieldCheck size={18} className="mr-2" weight="bold" />
                {busy ? "SAVING…" : "UPDATE PIN"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setOldPin(""); setNewPin(""); setConfirmPin(""); }}
                className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800 h-11"
                data-testid="pin-change-reset"
              >
                Clear
              </Button>
            </div>

            <div className="mt-4 text-[10px] font-mono-num tracking-widest text-slate-500">
              Tip: PIN badalne ke baad sabhi devices ko dobara PIN daalna padega.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

